import React, { useState, useEffect } from "react";
import { Check } from "lucide-react";

interface UserProfileSelectorProps<T> {
  title: string;
  items: T[];
  selectedItems: string[];
  itemKey: keyof T;
  onSelect: (item: T | null) => void;
  onClear: () => void;
  isDarkMode: boolean;
  isUserSelector?: boolean;
  isUserAlreadyAssociated?: (userId: string) => boolean;
  getAssociatedProfile?: (userId: string) => string | null;
}

function UserProfileSelector<T extends Record<string, any>>({
  title,
  items,
  selectedItems,
  itemKey,
  onSelect,
  onClear,
  isDarkMode,
  isUserSelector = false,
  isUserAlreadyAssociated,
  getAssociatedProfile
}: UserProfileSelectorProps<T>) {

  const [textConfig, setTextConfig] = useState<any>({});

  const fetchTextConfig = async () => {
    try {
      const response = await fetch("/text.json");
      const data = await response.json();
      setTextConfig(data);
    } catch (error) {
      console.error("Error loading text config:", error);
    }
  };

  useEffect(() => {
    fetchTextConfig();
  }, []);

  const getItemClassName = (isSelected: boolean, isDisabled: boolean = false) => `
    flex justify-between items-center p-2.5 rounded mb-0.5
    ${
      isDisabled
        ? "opacity-50 cursor-not-allowed bg-gray-600"
        : isSelected
        ? "bg-[#30503C] text-[#2ECC71] cursor-pointer"
        : isDarkMode
        ? "hover:bg-zinc-800 cursor-pointer"
        : "hover:bg-gray-200 cursor-pointer"
    }
  `;

  const getTextClassName = (isSelected: boolean, isDisabled: boolean = false) =>
    isDisabled 
      ? "text-gray-500" 
      : isSelected 
        ? "text-[#2ECC71]" 
        : isDarkMode 
          ? "text-gray-100" 
          : "text-gray-900";

  const getBorderClassName = () =>
    isDarkMode ? "border-zinc-700" : "border-gray-300";

  const handleItemSelect = (item: T) => {
    if (isUserSelector && isUserAlreadyAssociated && isUserAlreadyAssociated(String(item[itemKey]))) {
      return;
    }
    
    const isSelected = selectedItems.includes(String(item[itemKey]));
    if (isSelected) {
      onSelect(null);
    } else {
      onSelect(item);
    }
  };

  return (
    <div className="flex flex-col">
      <h3 className="text-base font-medium mb-2">{title}</h3>
      <div className={`flex-1 ${
        isDarkMode ? "bg-[#222222] border-zinc-700" : "bg-gray-100 border-gray-300"
      } rounded-lg border p-1`}>
        <div className={`max-h-[300px] overflow-y-auto border ${getBorderClassName()} rounded p-1`}>
          {items.length === 0 ? (
            <div className={`text-center p-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
              {textConfig.USER_PROFILE_SELECTOR_NO_ITEMS}
            </div>
          ) : (
                         items.map((item, index) => {
               const isSelected = selectedItems.includes(String(item[itemKey]));
               const isDisabled = isUserSelector && isUserAlreadyAssociated && isUserAlreadyAssociated(String(item[itemKey]));
               const associatedProfile = isDisabled && getAssociatedProfile ? getAssociatedProfile(String(item[itemKey])) : null;
               
               return (
                 <div
                   key={`${String(item[itemKey])}-${index}`}
                   onClick={() => handleItemSelect(item)}
                   className={getItemClassName(isSelected, isDisabled)}
                 >
                   <div className="flex-1 flex items-center justify-between">
                     <div className="flex flex-col">
                       <span className={getTextClassName(isSelected, isDisabled)}>
                         {String(item[itemKey])}
                       </span>
                       {isDisabled && associatedProfile && (
                         <span className="text-xs text-gray-500">
                           Ya asociado a: {associatedProfile}
                         </span>
                       )}
                     </div>
                     {isSelected && !isDisabled && (
                       <Check size={18} className="ml-2 text-[#2ECC71]" />
                     )}
                     {isDisabled && (
                       <span className="text-xs text-gray-500 ml-2">✓</span>
                     )}
                   </div>
                 </div>
               );
             })
          )}
        </div>
      </div>
    </div>
  );
}

export default UserProfileSelector;